/** Closed-field label when every regular option is selected and a `selectAll` option exists. */
export const SELECT_ALL_DISPLAY_LABEL = 'הכל';

/** MUI Select stores DOM values as strings — map option values for MenuItem wiring. */
export function wireOptionValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

export function wireOptionValues<T extends string | number>(values: T[] | undefined): string[] {
  return (values ?? []).map(value => wireOptionValue(value));
}

export function optionFromWireValue<
  T extends string | number,
  O extends { value: T }
>(key: string, options: readonly O[]): T | undefined {
  return options.find(option => wireOptionValue(option.value) === key)?.value;
}

export function optionsFromWireValues<
  T extends string | number,
  O extends { value: T }
>(keys: string | string[], options: readonly O[]): T[] {
  const keyList = typeof keys === 'string' ? keys.split(',') : keys;
  return keyList
    .map(key => optionFromWireValue(key, options))
    .filter((value): value is T => value !== undefined);
}

export function resolveClearedValue<
  T extends string | number,
  O extends { value: T }
>(options: readonly O[]): T | undefined {
  return options.some(option => wireOptionValue(option.value) === '') ? ('' as T) : undefined;
}

export function getRegularOptions<O extends { selectAll?: boolean }>(options: readonly O[]): O[] {
  return options.filter(option => !option.selectAll);
}

export function areAllRegularOptionsSelected<
  T extends string | number,
  O extends { value: T; selectAll?: boolean }
>(selected: T[] | undefined, options: readonly O[]): boolean {
  const regularOptions = getRegularOptions(options);
  if (regularOptions.length === 0) {
    return false;
  }

  const selectedSet = new Set((selected ?? []).map(value => wireOptionValue(value)));
  return regularOptions.every(option => selectedSet.has(wireOptionValue(option.value)));
}

export function getSelectAllCheckboxState<
  T extends string | number,
  O extends { value: T; selectAll?: boolean }
>(selected: T[] | undefined, options: readonly O[]): { checked: boolean; indeterminate: boolean } {
  const regularOptions = getRegularOptions(options);
  if (regularOptions.length === 0) {
    return { checked: false, indeterminate: false };
  }

  const selectedSet = new Set((selected ?? []).map(value => wireOptionValue(value)));
  const selectedCount = regularOptions.filter(option =>
    selectedSet.has(wireOptionValue(option.value))
  ).length;

  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }

  if (selectedCount === regularOptions.length) {
    return { checked: true, indeterminate: false };
  }

  return { checked: false, indeterminate: true };
}

export function getRegularOptionValues<O extends { value: string | number; selectAll?: boolean }>(
  options: readonly O[]
): O['value'][] {
  return getRegularOptions(options).map(option => option.value);
}

export function excludeSelectAllValues<
  T extends string | number,
  O extends { value: T; selectAll?: boolean }
>(values: T[] | undefined | null, options: readonly O[]): T[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter(
    value => !options.some(option => option.selectAll && option.value === value)
  );
}
